import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@ublend-npm/aulaui-core';
import type { Post } from '@app/types/core';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomContent from './CommonRoomContent';
import CommonRoomFeedResponsive from './CommonRoomFeedResponsive';
import CommonRoomSidebar from './CommonRoomSidebar';
import ScheduledPostsResponsive from './ScheduledPostsResponsive';
import type { CommonRoomView } from './types';
import type { ParticipantsPreviewParticipant } from './ParticipantsPreview/ParticipantsPreview.types';

interface CommonRoomResponsiveProps {
  fetchParticipantsPreview?: () => void;
  isFetchingParticipants?: boolean;
  participantTotal?: number;
  participants?: ParticipantsPreviewParticipant[];
  spaceId?: string;
  view?: CommonRoomView;
  posts?: Post[];
}

const CommonRoomResponsive = ({
  fetchParticipantsPreview,
  isFetchingParticipants = false,
  participantTotal = 0,
  participants = [],
  spaceId,
  view = 'feed',
  posts = [],
}: CommonRoomResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const requestedParticipantsForSpace = useRef<string>();

  useEffect(() => {
    if (
      !spaceId ||
      participantTotal === 0 ||
      participants.length >= Math.min(participantTotal, 4) ||
      isFetchingParticipants ||
      !fetchParticipantsPreview ||
      requestedParticipantsForSpace.current === spaceId
    ) {
      return;
    }

    requestedParticipantsForSpace.current = spaceId;
    fetchParticipantsPreview();
  }, [
    fetchParticipantsPreview,
    isFetchingParticipants,
    participantTotal,
    participants.length,
    spaceId,
  ]);

  return (
    <Box sx={styles.root} data-testid="common-room-responsive">
      <CommonRoomSidebar
        participantTotal={participantTotal}
        participants={participants}
        spaceId={spaceId}
      />

      <CommonRoomContent
        label={
          view === 'scheduled'
            ? 'Scheduled posts content'
            : 'Common Room content'
        }
      >
        {view === 'scheduled' ? (
          <ScheduledPostsResponsive />
        ) : (
          <CommonRoomFeedResponsive posts={posts} />
        )}
      </CommonRoomContent>
    </Box>
  );
};

export default CommonRoomResponsive;
